import { ClickTarget, SourcePage } from "@/services/Analytics/EventTypes";
import { useLogEvent } from "@/services/Analytics/useLogEvent";
import { t } from "@lingui/macro";
import { WahooWorkoutTypeFamily } from "@WahooFitness/cloud-client-types";
import { useCallback, useMemo } from "react";

import EducationRunningProfileSvg from "@/assets/EducationRunningProfile.svg";
// import Education3dpSnailGraphic from "./Education3dpSnailGraphic"; // TODO: Re-enable with second running card (PEAR-1583)
import EducationCyclingProfileSvg from "@/assets/EducationCyclingProfile.svg";
import EducationSpotifySvg from "@/assets/EducationSpotify.svg";
import EducationWahooMetricsSvg from "@/assets/EducationWahooMetrics.svg";
import Education4dpSnailGraphic from "./Education4dpSnailGraphic";

import { Box } from "@mui/material";
import Education3dpSnailGraphic from "./Education3dpSnailGraphic";
import { WfCardProps } from "@WahooFitness/redesignr/Components/WfCard/WfCard.js";
import { useNavigate } from "react-router";
import { formatVideoLength, getEducationVideos } from "@/components/EducationVideo/educationVideos";
import { useOpenEducationVideo } from "@/components/EducationVideo/useOpenEducationVideo";

function useEducationSection(): Partial<Record<WahooWorkoutTypeFamily, WfCardProps[]>> {
  const { logEvent } = useLogEvent();
  const navigate = useNavigate();
  const logRunningEducationAnalytics = useCallback(
    (educationType: string) => {
      logEvent("athlete_profile_running", {
        click_target: `${ClickTarget.education}_${educationType}`,
        source_page_name: SourcePage.athlete_profile_running,
      });
    },
    [logEvent]
  );

  const logCyclingEducationAnalytics = useCallback(
    (educationType: string) => {
      logEvent("athlete_profile_cycling", {
        click_target: `${ClickTarget.education}_${educationType}`,
        source_page_name: SourcePage.athlete_profile_cycling,
      });
    },
    [logEvent]
  );

  const openEducationVideo = useOpenEducationVideo();

  const handleInternalNavigation = useCallback(
    (url: string) => {
      navigate(url);
    },
    [navigate]
  );

  const cardsByType = useMemo<Partial<Record<WahooWorkoutTypeFamily, WfCardProps[]>>>(() => {
    const athleteProfileVideo = getEducationVideos().athlete_profile;
    const videoCard = (logAnalytics: (educationType: string) => void): WfCardProps => ({
      imageAsset: athleteProfileVideo.thumbnail,
      title: athleteProfileVideo.title,
      mediaType: "video",
      mediaLabel: t`Video`,
      timeLabel: formatVideoLength(athleteProfileVideo.lengthSeconds),
      onClick: () => {
        logAnalytics("video_athlete_profile");
        openEducationVideo("athlete_profile");
      },
    });

    const running: WfCardProps[] = [
      videoCard(logRunningEducationAnalytics),
      {
        imageAsset: EducationRunningProfileSvg,
        title: t`Understanding core metrics of Running Athlete Profile.`,
        timeLabel: t`1 min read`,
        onClick: () => logRunningEducationAnalytics("about_3dp"),
        internalPath: "/threedp-education?standAlone",
        handleInternalNavigation: () => handleInternalNavigation("/threedp-education?standAlone"),
      },
      {
        headerComponent: (
          <Box
            component="div"
            role="img"
            aria-label={t`Running Athlete Profile graphic`}
            sx={{
              width: "100%",
              height: "100%",
              "& > svg": {
                width: "100%",
                height: "100%",
                display: "block",
              },
            }}
          >
            <Education3dpSnailGraphic />
          </Box>
        ),
        title: t`Running Athlete Profile: the future of running training`,
        timeLabel: t`5 min read`,
        onClick: () => logRunningEducationAnalytics("blog_3dp"),
        externalLink:
          "https://www.wahoofitness.com/blog/the-end-of-one-number-training-why-wahoos-3dp-is-about-to-change-how-runners-train-forever/",
      },
    ];

    const cycling: WfCardProps[] = [
      videoCard(logCyclingEducationAnalytics),
      {
        imageAsset: EducationCyclingProfileSvg,
        title: t`Understanding core metrics of Cycling Athlete Profile.`,
        timeLabel: t`1 min read`,
        onClick: () => logCyclingEducationAnalytics("about_4dp"),
        internalPath: "/fourdp-education?standAlone",
        handleInternalNavigation,
      },
      {
        imageAsset: EducationSpotifySvg,
        title: t`Wahoo Training Metrics podcast`,
        timeLabel: t`51 min 9 sec`,
        onClick: () => logCyclingEducationAnalytics("podcast_4dp"),
        externalLink: "https://open.spotify.com/episode/6kBST0oXwIHXAOGuCYuyrl?si=e1fbf9f11e1548e9",
      },
      {
        imageAsset: EducationWahooMetricsSvg,
        title: t`Why Wahoo Metrics is the best training power methodology?`,
        timeLabel: t`6 min read`,
        onClick: () => logCyclingEducationAnalytics("blog_4dp"),
        externalLink:
          "https://www.wahoofitness.com/blog/wahoo-athlete-profile-why-4dp-is-the-best-cycling-training-power-methodology/",
      },
      {
        headerComponent: (
          <Box
            component="div"
            role="img"
            aria-label={t`Cycling Athlete Profile graphic`}
            sx={{
              width: "100%",
              height: "100%",
              "& > svg": {
                width: "100%",
                height: "100%",
                display: "block",
              },
            }}
          >
            <Education4dpSnailGraphic />
          </Box>
        ),
        title: t`Learn more about FTP, MAP, AC, NM.`,
        timeLabel: t`1 min read`,
        onClick: () => logCyclingEducationAnalytics("support_4dp"),
        externalLink:
          "https://support.wahoofitness.com/hc/en-us/articles/360021387420-Learn-about-4DP",
      },
    ];

    return {
      [WahooWorkoutTypeFamily.Running]: running,
      [WahooWorkoutTypeFamily.Cycling]: cycling,
    };
  }, [
    logCyclingEducationAnalytics,
    logRunningEducationAnalytics,
    handleInternalNavigation,
    openEducationVideo,
  ]);

  return cardsByType;
}

export default useEducationSection;
